import type { PriceLevelInputDataInput } from "~/gql/graphqlBase.generated";
import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { OrderVolumeDiscountFormData } from "~fulfill/sales/pricing/helpers/orderVolumeDiscount";
import type { PriceGroupDerivationFormData } from "~fulfill/sales/pricing/helpers/priceGroupDerivation";
import type { PriceGroupQualificationFormData } from "~fulfill/sales/pricing/helpers/priceGroupQualification";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import { TargetType } from "~/gql/graphqlBase.generated";
import { hasPriceGroupDerivationChanged } from "~fulfill/sales/pricing/helpers/priceGroupDerivation";

export const PRICE_GROUP_SOURCE_KIND = "priceGroup";

export const PRICE_GROUP_TARGET_KIND = "priceGroup";

export const PRICE_LEVEL_TARGET_KIND = "priceLevel";

export const PRICE_GROUPS_BASE_PATH = "/fulfill/sales/pricing/groups";

export const PRICE_GROUPS_DOCK_PANEL_ID = "sales-pricing";

const PRICE_GROUP_ROUTE_PATTERN = /^\/fulfill\/sales\/pricing\/groups\/group\/([^/?#]+)$/;

export interface PriceGroupCoreDraft {
	derivation: PriceGroupDerivationFormData;
	name: string;
}

export const EMPTY_CORE_DRAFT: PriceGroupCoreDraft = {
	derivation: { derivedFromPriceGroupId: null, derivedPercentOff: null },
	name: ""
};

export const hasCoreDraftChanged = (from: PriceGroupCoreDraft, to: PriceGroupCoreDraft): boolean =>
	from.name.trim() !== to.name.trim() || hasPriceGroupDerivationChanged(from.derivation, to.derivation);

export interface PriceLevelDialogDraft {
	formData: PriceLevelInputDataInput;
	pool: string;
	priceLevelId: null | string;
}

export interface PriceGroupDraft {
	core: null | PriceGroupCoreDraft;
	levelDialog?: null | PriceLevelDialogDraft;
	orderVolumeDiscount: null | OrderVolumeDiscountFormData;
	qualification: null | PriceGroupQualificationFormData;
}

export interface PriceGroupSourceCheckpoint {
	draft: PriceGroupDraft;
	priceGroupId: string;
	priceGroupName: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isPriceLevelDialogDraft = (value: unknown): value is PriceLevelDialogDraft =>
	isRecord(value) &&
	isRecord(value.formData) &&
	typeof value.formData.targetId === "string" &&
	typeof value.pool === "string" &&
	(value.priceLevelId === null || typeof value.priceLevelId === "string");

export const isPriceGroupSourceCheckpoint = (value: unknown): value is PriceGroupSourceCheckpoint =>
	isRecord(value) &&
	typeof value.priceGroupId === "string" &&
	typeof value.priceGroupName === "string" &&
	isRecord(value.draft) &&
	(value.draft.core === null ||
		(isRecord(value.draft.core) &&
			typeof value.draft.core.name === "string" &&
			isRecord(value.draft.core.derivation))) &&
	(value.draft.levelDialog === undefined ||
		value.draft.levelDialog === null ||
		isPriceLevelDialogDraft(value.draft.levelDialog));

export const seedCreatedPriceLevelTarget = (
	levelDialog: PriceLevelDialogDraft,
	createdItemId: null | string
): PriceLevelDialogDraft =>
	createdItemId && levelDialog.formData.type === TargetType.Item
		? { ...levelDialog, formData: { ...levelDialog.formData, targetId: createdItemId, targetName: "" } }
		: levelDialog;

export const buildPriceGroupPath = (priceGroupId: string): string =>
	`${PRICE_GROUPS_BASE_PATH}/group/${encodeURIComponent(priceGroupId)}`;

export const matchPriceGroupReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = PRICE_GROUP_ROUTE_PATTERN.exec(pathname);
	return match ? { targetId: decodeURIComponent(match[1]), targetKind: PRICE_GROUP_TARGET_KIND } : null;
};

export const priceGroupReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => buildPriceGroupPath(targetId),
	matchesRoute: matchPriceGroupReceiverRoute
};

export const buildPriceLevelTarget = (priceGroupId: string, priceLevelId: string): ContinuationTarget => ({
	listFilters: { priceLevelId },
	targetId: priceGroupId,
	targetKind: PRICE_LEVEL_TARGET_KIND
});

export const readTargetPriceLevelId = (target: ContinuationTarget): null | string =>
	target.targetKind === PRICE_LEVEL_TARGET_KIND ? (target.listFilters?.priceLevelId ?? null) : null;

export const priceLevelReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => buildPriceGroupPath(targetId),
	matchesRoute: () => null
};

const priceGroupEditorKey = (priceGroupId: string): string => `price-group:${priceGroupId}`;

export default priceGroupEditorKey;
